import React from 'react';
import { SpacingStack } from '@withjoy/joykit';
import { LayoutShell, ContentHeader, CtaGroup, PrimaryCta, DonationProgressBar, IntegratedGiftCard, SecondaryCta } from '../../components';
import { useGiftAmountController } from './GiftAmount.controller';
import { preventNonNumericalInput } from '@shared/utils/preventNonNumericalInput';
import { useHistory, useRouterHelper, useTranslation } from '@shared/core';
import { DonationAmountInput } from './DonationAmountInput';
import { GiftAmountSelectors } from './GiftAmount.selectors';
import { CustomItemDataType } from '@graphql/generated-base';
import MustHaveChip from '../../components/MustHaveChip';
import { LoadingExpressCheckoutElement } from '@shared/core/stripe';
import { useRoutePaths } from '@apps/registry/guest/GuestRegistry.routes';
import { useGuestRegistryTelemetry } from '@apps/registry/guest/GuestRegistry.telemetry';
import { useEventInfo } from '@shared/utils/eventInfo';
import { DEFAULT_CURRENCY } from '@shared/utils/currency/constants';
import { LoadingRegistryCheckoutSettings } from '@apps/registry/common/components/LoadingRegistryCheckoutSettings';

export const GiftAmount = () => {
  const {
    goalAmount,
    amountDonated,
    currencyCode,
    amount,
    formattedAmount,
    suggestedDonation,
    formik,
    setAmount,
    currencySymbol,
    stillNeededOptions,
    registryItem,
    showProgressBar,
    enableJoyCredit,
    onSubmit,
  } = useGiftAmountController();
  const { productData, donationFund, mustHave } = registryItem;

  const isCharity = donationFund?.fundType === CustomItemDataType.charity;
  const routePaths = useRoutePaths();
  const router = useRouterHelper();
  const history = useHistory();
  const { t } = useTranslation('guestRegistry');
  const { fieldAmountLabel } = t('giveGiftDialog');
  const { giveNow, giveNowWithAmount, additionalPaymentOptions } = t('checkoutDialog', 'screens', 'giftAmount');
  const telemetry = useGuestRegistryTelemetry();
  const { eventInfo } = useEventInfo();
  const eventId = eventInfo?.eventId;

  const checkoutFallback = (
    <PrimaryCta
      {...GiftAmountSelectors.GiveNowButton}
      onClick={() => onSubmit('full')}
    >
      {amount ? giveNow({ amount: formattedAmount }) : giveNowWithAmount()}
    </PrimaryCta>
  );

  // Check if this is a US wedding for showing amount buttons
  const isUSWedding = currencyCode === DEFAULT_CURRENCY && eventInfo?.eventType === 'wedding';
  const isCashFund = !suggestedDonation && donationFund?.fundType !== CustomItemDataType.gift;
  const shouldShowAmountButtons = isUSWedding && isCashFund && Number(goalAmount || 0) >= 100;

  return (
    <LayoutShell {...GiftAmountSelectors.ContainerElement}>
      <LayoutShell.Header mobilePlacement={isCharity ? 'static' : 'float'} />
      <LayoutShell.Body>
        <LayoutShell.MediaPanel>
          <IntegratedGiftCard mobileRenderBehavior="fullBleed" />
        </LayoutShell.MediaPanel>
        <LayoutShell.ContentPanel>
          {mustHave && <MustHaveChip />}
          <ContentHeader>
            <ContentHeader.Hed>{productData.title}</ContentHeader.Hed>
            {donationFund?.description && <ContentHeader.Dek isCollapsible={true}>{donationFund.description}</ContentHeader.Dek>}
          </ContentHeader>
          <SpacingStack
            spacing={6}
            marginTop={6}
          >
            {showProgressBar && (
              <DonationProgressBar
                amountDonated={amountDonated}
                totalAmount={goalAmount || ''}
                currencyCode={currencyCode}
                userContribution={amount || 0}
              />
            )}
            <form>
              <DonationAmountInput
                isDisabled={false}
                fieldAmountLabel={fieldAmountLabel()}
                shouldShowAmountButtons={shouldShowAmountButtons}
                {...{ suggestedDonation, formik, stillNeededOptions, setAmount, preventNonNumericalInput, currencySymbol }}
              />
              <CtaGroup marginTop={7}>
                <LoadingRegistryCheckoutSettings
                  eventId={eventId}
                  fallbackContent={checkoutFallback}
                >
                  <LoadingExpressCheckoutElement
                    onParentClick={event => {
                      if (amount) {
                        telemetry.expressCheckoutClicked({ ...event, source: 'CheckoutDialog_GiftAmount' });
                        history.push(router.buildPath(routePaths.itemCheckout.goToPath(registryItem.id, Math.round(amount * 100).toString())));
                      }
                    }}
                    showFallback={!enableJoyCredit}
                    disablePaymentEvents={true}
                    onConfirm={() => {}}
                    onReady={event => telemetry.expressCheckoutElementReady({ ...event, source: 'CheckoutDialog_GiftAmount' })}
                    options={{ buttonHeight: 50 }}
                    upperContent={
                      <SecondaryCta
                        {...GiftAmountSelectors.GiveNowButton}
                        onClick={() => onSubmit('full')}
                      >
                        {additionalPaymentOptions()}
                      </SecondaryCta>
                    }
                    fallbackContent={checkoutFallback}
                  />
                </LoadingRegistryCheckoutSettings>
              </CtaGroup>
            </form>
          </SpacingStack>
        </LayoutShell.ContentPanel>
      </LayoutShell.Body>
    </LayoutShell>
  );
};
